import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { type EffortLevel, effortLevels } from "../aiProviders";
import { writeCommitFile } from "../commitFiles";
import { DEFAULT_WORKFLOW_RETENTION_SECONDS } from "../executionRetention";
import { type Schema, zql } from "../generatedDrizzleSchema";
import {
  DEFAULT_WORKFLOW_NAME,
  sanitizeWorkflowName,
  uniqueWorkflowName,
} from "../workflowName";
import { assertAttachmentsSupported } from "./attachmentValidation";
import { mutator } from "./mutator";

export const WORKFLOW_README_STUB =
  "Describe what this workflow does and how to use it.\n";

const attachmentSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9_-]+$/),
  fileName: z.string(),
  mediaType: z.string(),
  sizeBytes: z.number(),
  data: z.string(),
});

export const createWorkflowSchema = z.object({
  id: z.string().regex(/^[a-zA-Z0-9_-]+$/),
  spaceId: z.string(),
  branchId: z.string(),
  commitId: z.string(),
  chatId: z.string(),
  userEntryId: z.string(),
  name: z.string(),
  // Omitted when creating an empty workflow to build directly (no AI turn, no
  // initial user message); present when the AI should build from the prompt.
  prompt: z.string().optional(),
  isDictated: z.boolean().optional(),
  planMode: z.boolean().optional(),
  effort: z.enum(effortLevels).optional(),
  chatModel: z.string().optional(),
  attachments: z.array(attachmentSchema).optional(),
});

export type SkeletonAttachment = {
  id: string;
  fileName: string;
  mediaType: string;
  sizeBytes: number;
};

export async function insertWorkflowSkeleton(opts: {
  tx: Transaction<Schema>;
  userId: string;
  tenantId: string;
  workflowId: string;
  spaceId: string;
  branchId: string;
  commitId: string;
  chatId: string;
  userEntryId: string;
  name: string;
  prompt?: string;
  isDictated?: boolean;
  planMode?: boolean;
  effort?: EffortLevel;
  attachments?: readonly SkeletonAttachment[];
}): Promise<void> {
  const {
    tx,
    userId,
    tenantId,
    workflowId,
    spaceId,
    branchId,
    commitId,
    chatId,
    userEntryId,
    name,
    prompt,
    isDictated,
    planMode,
    effort,
    attachments,
  } = opts;
  const now = Date.now();

  const trimmedName = name.trim();
  const needsName = trimmedName === "" || trimmedName === DEFAULT_WORKFLOW_NAME;
  const siblings = await tx.run(
    zql.workflows.where("spaceId", spaceId).where("deletedAt", "IS", null)
  );
  const finalName = uniqueWorkflowName(
    needsName ? DEFAULT_WORKFLOW_NAME : sanitizeWorkflowName(trimmedName),
    siblings.map((w) => w.name)
  );

  await tx.mutate.workflows.insert({
    id: workflowId,
    spaceId,
    name: finalName,
    needsName,
    createdAt: now,
    updatedAt: now,
    createdByUserId: userId,
    autofixEnabled: true,
    autoImproveEnabled: false,
    retentionSeconds: DEFAULT_WORKFLOW_RETENTION_SECONDS,
    tenantId,
  });

  await tx.mutate.spaces.update({
    id: spaceId,
    updatedAt: now,
  });

  await tx.mutate.workflowBranches.insert({
    id: branchId,
    workflowId,
    userId,
    name: "First draft",
    headCommitId: commitId,
    createdAt: now,
    lastChatEntryAt: prompt ? now : null,
    lastChatEntryType: prompt ? "UserMessage" : null,
    // respondingAt marks an AI turn in flight; an empty workflow has none.
    respondingAt: prompt ? now : null,
    tenantId,
  });

  await tx.mutate.workflowCommits.insert({
    id: commitId,
    branchId,
    parentCommitId: null,
    chatId,
    message: null,
    createdAt: now,
    createdByUserId: userId,
    tenantId,
  });

  await writeCommitFile(
    { tx, commitId, tenantId },
    "README.md",
    WORKFLOW_README_STUB
  );

  await tx.mutate.chats.insert({
    id: chatId,
    commitId,
    createdAt: now,
    lastChatEntryType: prompt ? "UserMessage" : null,
    respondingAt: prompt ? now : null,
    tenantId,
  });

  // An empty workflow has no opening message, no AI turn, and no contributor
  // yet — those appear when the builder first messages or edits.
  if (!prompt) return;

  await tx.mutate.chatEntries.insert({
    id: userEntryId,
    chatId,
    entryType: "UserMessage",
    createdAt: now,
    previousEntryId: null,
    tenantId,
  });
  await tx.mutate.userMessages.insert({
    entryId: userEntryId,
    userId,
    text: prompt,
    isDictated: isDictated ?? null,
    planMode: planMode ?? null,
    effort: effort ?? null,
    tenantId,
  });
  await tx.mutate.branchContributors.insert({
    branchId,
    workflowId,
    userId,
    lastMessagedAt: now,
    tenantId,
  });
  for (const att of attachments ?? []) {
    await tx.mutate.attachments.insert({
      id: att.id,
      entryId: userEntryId,
      fileName: att.fileName,
      mediaType: att.mediaType,
      sizeBytes: att.sizeBytes,
      tenantId,
    });
  }
}

export const createWorkflow = mutator({
  redactKeysOnSecret: ["prompt"],
  stripKeys: { attachments: ["data"] },
  schema: createWorkflowSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      id,
      space,
      branchId,
      commitId,
      chatId,
      userEntryId,
      name,
      prompt,
      isDictated,
      planMode,
      effort,
      chatModel,
      attachments,
    },
  }) => {
    await assertAttachmentsSupported(tx, attachments, chatModel);
    await insertWorkflowSkeleton({
      tx,
      userId: ctx.userId,
      tenantId: ctx.tenantId,
      workflowId: id,
      spaceId: space.id,
      branchId,
      commitId,
      chatId,
      userEntryId,
      name,
      prompt,
      isDictated,
      planMode,
      effort,
      attachments,
    });
  },
});
